<template>
  <modal-template :id="id" :name="name" :showModal="showModal" v-on:hide-modal="close()">
    <template slot="modalContent">
      <v-tabs v-model="activeTab" grow>
        <v-tab key="data" ripple>Data</v-tab>
        <v-tab key="preferences" ripple>Preference Parameters</v-tab>
        <v-tab-item key="data">
          <v-tabs v-model="activeTabData" grow color="info" dark>
            <v-tab key="actions" ripple>Actions</v-tab>
            <v-tab key="criteria" ripple>Criteria</v-tab>
            <v-tab key="performanceTable" ripple>Performance Table</v-tab>

            <v-tab-item key="actions">
              <div id="subheader">
                <v-data-table id="actions-table" :items="actions" hide-actions class="elevation-5">
                  <template slot="no-data"><tr><td colspan="100%" class="text-xs-center">Empty</td></tr></template>
                  <template slot="headers" slot-scope="props">
                    <th class="text-xs-left" style="width: 20%">Name</th>
                    <th class="text-xs-left">Description</th>
                    <th style="width: 70px;"></th>
                  </template>
                  <template slot="items" slot-scope="props">
                    <td>
                      <v-text-field placeholder="Insert value" v-model="props.item['Name']" hide-details></v-text-field>
                    </td>
                    <td>
                      <v-textarea placeholder="Insert value" v-model="props.item['Description']" hide-details rows="1" maxlength="150"></v-textarea>
                    </td>
                    <td>
                      <v-btn icon @click="removeActionsLine(props.item)"><v-icon color="pink">delete</v-icon></v-btn>
                    </td>
                  </template>
                </v-data-table>
                <v-btn @click="addActionsLine(null)" color="info">New Action</v-btn>
              </div>
            </v-tab-item>

            <v-tab-item key="criteria">
              <div id="subheader">
                <v-data-table id="criteria-table" :items="criteria" hide-actions class="elevation-5">
                  <template slot="no-data"><tr><td colspan="100%" class="text-xs-center">Empty</td></tr></template>
                  <template slot="headers" slot-scope="props">
                    <th class="text-xs-left" style="width: 20%">Name</th>
                    <th class="text-xs-left" style="width: 25%">Description</th>
                    <th class="text-xs-left column-wrap">Direction</th>
                    <th class="text-xs-left column-wrap">Scale Type</th>
                    <th class="text-xs-left">Min</th>
                    <th class="text-xs-left">Max</th>
                    <th class="text-xs-left column-wrap">Num Levels</th>
                    <th style="width: 70px;"></th>
                  </template>
                  <template slot="items" slot-scope="props">
                    <td>
                      <v-text-field placeholder="Insert value" v-model="props.item['Name']" hide-details></v-text-field>
                    </td>
                    <td>
                      <v-textarea placeholder="Insert value" v-model="props.item['Description']" hide-details rows="1" maxlength="150"></v-textarea>
                    </td>
                    <td>
                      <v-select placeholder="Please select" :items="['Maximization', 'Minimization']" v-model="props.item['Direction']" hide-details>
                        {{ props.item['Direction'] }}
                      </v-select>
                    </td>
                    <td>
                      <v-select placeholder="Please select" :items="['Cardinal', 'Ordinal']" v-model="props.item['Scale Type']" hide-details>
                        {{ props.item['Scale Type'] }}
                      </v-select>
                    </td>
                    <td>
                      <v-text-field v-if="props.item['Scale Type']=='Cardinal'" placeholder="Insert value" v-model="props.item['Min']"
                                    type="number" hide-details></v-text-field>
                    </td>
                    <td>
                      <v-text-field v-if="props.item['Scale Type']=='Cardinal'" placeholder="Insert value" v-model="props.item['Max']"
                                    type="number" hide-details></v-text-field>
                    </td>
                    <td>
                      <v-text-field v-if="props.item['Scale Type']=='Ordinal'" placeholder="Insert value" v-model="props.item['Num Levels']"
                                    type="number" :rules="[rules.positiveInt]" validate-on-blur hide-details></v-text-field>
                    </td>
                    <td>
                      <v-btn icon @click="removeCriteriaLine(props.item)"><v-icon color="pink">delete</v-icon></v-btn>
                    </td>
                  </template>
                </v-data-table>
                <v-btn @click="addCriteriaLine(null)" color="info">New Criterion</v-btn>
              </div>
            </v-tab-item>

            <v-tab-item key="performanceTable">
              <div id="subheader">
                <v-data-table id="performance-table" :items="performanceTable" hide-actions class="elevation-5">
                  <template slot="no-data"><tr><td colspan="100%" class="text-xs-center">Empty</td></tr></template>
                  <template slot="headers" slot-scope="props">
                    <th class="text-xs-left" style="width: 20%">Name</th>
                    <th class="text-xs-left" v-for="crit in criteria" :key="crit['Name']">{{ crit['Name'] }}</th>
                  </template>
                  <template slot="items" slot-scope="props">
                    <td class="text-xs-left">{{ props.item['Name'] }}</td>
                    <td v-for="crit in criteria" :key="crit['Name']">
                      <v-tooltip top>
                        <v-text-field slot="activator" placeholder="Insert value" v-model="props.item[crit['Name']]" type="number" hide-details
                                      :rules="[rules[crit['Name']]]" validate-on-blur @change="checkIfDirty('performanceTable')"></v-text-field>
                        <span v-if="crit['Scale Type']==='Cardinal'">Min: {{ crit['Min'] }}, Max: {{ crit['Max'] }}</span>
                        <span v-else-if="crit['Scale Type']==='Ordinal'">Num Levels: {{ crit['Num Levels'] }}</span>
                      </v-tooltip>
                    </td>
                  </template>
                </v-data-table>
              </div>
            </v-tab-item>
          </v-tabs>
        </v-tab-item>

        <v-tab-item key="preferences">
          <v-tabs v-model="activeTabPreferences" color="info" grow dark>
            <v-tab key="referenceActions" ripple>Reference Actions</v-tab>
            <v-tab key="weights" ripple>Weights</v-tab>
            <v-tab key="thresholds" ripple>Thresholds</v-tab>
            <v-tab key="credibility" ripple>Credibility</v-tab>

            <v-tab-item key="referenceActions">
              <div id="subheader">
                <v-data-table id="ref-actions-table" :items="referenceActions" hide-actions class="elevation-5">
                  <template slot="no-data"><tr><td colspan="100%" class="text-xs-center">Empty</td></tr></template>
                  <template slot="headers" slot-scope="props">
                    <th class="text-xs-left" style="width: 20%">Category</th>
                    <th class="text-xs-left" style="width: 10%">Name</th>
                    <th class="text-xs-left" v-for="crit in criteria" :key="crit['Name']">{{ crit['Name'] }}</th>
                    <th style="width: 70px;"></th>
                  </template>
                  <template slot="items" slot-scope="props">
                    <td>
                      <v-combobox placeholder="Select or create new" v-model="props.item['Category']" :items="referenceActions.map(a => a['Category']).filter(x => x)"
                                  hide-details></v-combobox>
                    </td>
                    <td>
                      <v-text-field placeholder="Insert value" v-model="props.item['Name']" hide-details></v-text-field>
                    </td>
                    <td v-for="crit in criteria" :key="crit['Name']">
                      <v-tooltip top>
                        <v-text-field slot="activator" placeholder="Insert value" v-model="props.item[crit['Name']]" type="number" hide-details
                                      :rules="[rules[crit['Name']]]" validate-on-blur></v-text-field>
                        <span v-if="crit['Scale Type']==='Cardinal'">Min: {{ crit['Min'] }}, Max: {{ crit['Max'] }}</span>
                        <span v-else-if="crit['Scale Type']==='Ordinal'">Num Levels: {{ crit['Num Levels'] }}</span>
                      </v-tooltip>
                    </td>
                    <td>
                      <v-btn icon @click="removeReferenceActionsLine(props.item)"><v-icon color="pink">delete</v-icon></v-btn>
                    </td>
                  </template>
                </v-data-table>
                <v-alert :value="true" type="info" outline>Worst category at the top, best at the bottom.</v-alert>
                <v-btn @click="addReferenceActionsLine(null)" color="info">New Reference Action</v-btn>
              </div>
            </v-tab-item>

            <v-tab-item key="weights">
              <div id="subheader">
                <v-data-table id="weights-table" :items="weights" hide-actions class="elevation-5">
                  <template slot="no-data"><tr><td colspan="100%" class="text-xs-center">Empty</td></tr></template>
                  <template slot="headers" slot-scope="props">
                    <th class="text-xs-left" v-for="crit in criteria" :key="crit['Name']">{{ crit['Name'] }}</th>
                  </template>
                  <template slot="items" slot-scope="props">
                    <td v-for="crit in criteria" :key="crit['Name']">
                      <v-text-field placeholder="Insert value" type="number" min="0" v-model="props.item[crit['Name']]"
                                    @change="checkIfDirty('weights')"></v-text-field>
                    </td>
                  </template>
                </v-data-table>
                <v-btn id="new1" @click="addWeights()" color="info">New Weights</v-btn>
              </div>
            </v-tab-item>

            <v-tab-item key="thresholds">
              <div id="subheader">
                <v-data-table id="thresholds-table" :items="thresholds" hide-actions class="elevation-5">
                  <template slot="no-data"><tr><td colspan="100%" class="text-xs-center">Empty</td></tr></template>
                  <template slot="headers" slot-scope="props">
                    <th class="text-xs-left" style="width: 20%">Threshold</th>
                    <th class="text-xs-left" v-for="crit in criteria" :key="crit['Name']">{{ crit['Name'] }}</th>
                  </template>
                  <template slot="items" slot-scope="props">
                      <td class="text-xs-left">{{ props.item['Threshold'] }}</td>
                                          <td v-for="crit in criteria" :key="crit['Name']">
                      <v-tooltip top>
                        <v-text-field slot="activator" placeholder="Insert value" v-model="props.item[crit['Name']]" type="number" hide-details
                                      :rules="[rules[crit['Name']]]" validate-on-blur></v-text-field>
                        <span v-if="crit['Scale Type']==='Cardinal'">Min: 0, Max: {{ crit['Max'] }}</span>
                        <span v-else-if="crit['Scale Type']==='Ordinal'">Num Levels: {{ crit['Num Levels'] }}</span>
                      </v-tooltip>
                    </td>
                  </template>
                </v-data-table>
                <v-alert :value="true" type="info" outline>For no veto threshold leave the space in blank.</v-alert>
                <v-btn id="new2" @click="addThresholds()" color="info">New Thresholds</v-btn>
              </div>
            </v-tab-item>

            <v-tab-item key="credibility">
              <div id="subheader">
                <v-text-field class="input-class" label="Credibility Level" v-model="credibility" type="number" step="0.01" min="0.50" max="1.00" @change="changedProp('credibility')"></v-text-field>
              </div>
            </v-tab-item>

          </v-tabs>
        </v-tab-item>
      </v-tabs>
    </template>
  </modal-template>
</template>

<script>
import {EventBus} from '@/event-bus.js'
import ModalTemplate from '@/components/Workspace/Methods/ModalTemplate'

export default {
  components: {
    ModalTemplate,
  },
  data: () => ({
    showModal: false,
    activeTab: null,
    activeTabData: null,
    activeTabPreferences: null,
    tabsData: ['actions', 'criteria', 'performanceTable'],
    tabsPreferences: ['referenceActions', 'weights', 'thresholds', 'credibility'],

    actions: [],
    criteria: [],
    performanceTable: [],
    referenceActions: [],
    weights: [],
    thresholds: [],
    credibility: [],

    dirty: {},
    dataFromConnection: false,
    cleaningUp: false,
    createNewCategory: false,
    newCategory: '',

    rules: {
      positiveInt: value => /^\+?(0|[1-9]\d*)$/.test(value) || value==='' || this.a.methods.notify('Must be a positive integer'),
    }
  }),
  props: {
    id: String,
    name: String,
  },
  methods: {
    notify(msg) {
      EventBus.$emit('send-notification', {text: msg});
      return false;
    },
    clone(object) {
      return JSON.parse(JSON.stringify(object))
    },
    validateData() {
      var errors = {};
      
      // performance table
      var actionNames = this.actions.map(c => c['Name']);
      for(var action of this.performanceTable) {
        if(!actionNames.includes(action['Name'])) {
          errors.performanceTable = 'actions referenced in performance table missing';
        }
      }
      // thresholds
      if(this.thresholds.length){
        for(var crit of this.criteria){
        var criterion = crit['Name'];
        if(Number(this.thresholds[0][criterion]) < Number(this.thresholds[1][criterion]) ||
           Number(this.thresholds[0][criterion]) < 0 ||
           Number(this.thresholds[1][criterion]) < 0 ||
           ( Number(this.thresholds[2][criterion]) < Number(this.thresholds[0][criterion]) &&
             Number(this.thresholds[2][criterion]) != 0 )
           ){
          errors.thresholds = 'thresholds must respect some relation rules';
        }
        }
      }
      // credibility
      if(this.credibility.length){
        if(this.credibility < 0.5 || this.credibility > 1){
        errors.credibility = 'credibility must be between 0.5 and 1';
        }
      }
      
      for(var param of this.tabsData.concat(this.tabsPreferences)) {
        if(errors[param]) {
          this.notify(this.name + ': ' + errors[param]);
          EventBus.$emit('set-endpoint-mode', {endpoint: this.id+'-'+param, mode: 'error'});
        } else if(!(typeof this[param]!=='undefined' && this[param].length===0))
          EventBus.$emit('set-endpoint-mode', {endpoint: this.id+'-'+param, mode: 'active'});
      }
      
    },
    saveModuleData() {
      var headers = ['Action','Minimum','Maximum'];
      var methodAttributes = {};
      methodAttributes.input = {
        actions: this.actions,
        criteria: this.criteria,
        performanceTable: this.performanceTable,
        referenceActions: this.referenceActions,
        weights: this.weights,
        thresholds: this.thresholds,
        credibility: this.credibility
      };
      methodAttributes.output = {
        headers: headers,
        description: "Assignment Results"
      };
      methodAttributes.id = this.id;
      this.validateData();
      EventBus.$emit('save-module-data', methodAttributes);
    },
    setPropertyFromInputFile(mod) {
      if(mod.newConnection) this.dataFromConnection = true;
      if(typeof mod.value!=='undefined' && mod.value.length===0) this.cleaningUp = true;
      if(['criteria', 'actions', 'referenceActions'].includes(mod.property)) {
        var val = this.clone(mod.value);
        while(this[mod.property].length>0)
          this['remove' + mod.property.charAt(0).toUpperCase() + mod.property.slice(1) + 'Line'](this[mod.property][0]);
        for(var element of val)
          this['add' + mod.property.charAt(0).toUpperCase() + mod.property.slice(1) + 'Line'](element);
      } else
        this[mod.property] = this.clone(mod.value);
      if(this.cleaningUp) this.dirty[mod.property] = false;
      else this.dirty[mod.property] = true;
      this.cleaningUp = false;
      this.saveModuleData();
      if(mod.newConnection) this.dataFromConnection = false;
    },
    checkIfDirty(prop) {
      if(!this.dirty[prop]) {
        this.dirty[prop] = true;
        var offset = this.tabsData.indexOf(prop)!==-1 ? this.tabsData.indexOf(prop) : 3+this.tabsPreferences.indexOf(prop);
        var obj = {target: this.id, property: prop, value: this[prop], yOffset: offset};
        EventBus.$emit('generate-data-module', obj);
        return true;
      } else return false;
    },
    changedProp(prop) {
      if(!this.dataFromConnection) this.checkIfDirty(prop);
    },
    addCriteriaLine(line) {
      var newCrit = line ? line : {'Name': '', 'Description': '', 'Direction': 'Maximization', 'Scale Type': 'Cardinal', 'Min': '', 'Max': '', 'Num Levels': ''};
      this.criteria.push(newCrit);
      this.rules[newCrit['Name']] = (value) => {
        if(value === '')
          return true;
        else if(newCrit['Scale Type']==='Cardinal') {
          var val = parseFloat(value);
          if(newCrit['Min'].length===0 || newCrit['Max'].length===0)
            return true;
          else if(val < newCrit['Min'] || val > newCrit['Max'])
            return this.notify('Must be a value between ' + newCrit['Min'] + ' and ' + newCrit['Max']);
        } else if(newCrit['Scale Type']==='Ordinal') {
          var val = parseInt(value);
          if(newCrit['Num Levels'].length===0)
            return true;
          else if(val < 1 || val > newCrit['Num Levels'] || !Number.isInteger(val)) {
            return this.notify('Must be a positive integer less than ' + newCrit['Num Levels']);}
        }
        return true;
      };
      if(!this.dataFromConnection) this.checkIfDirty('criteria');
    },
    removeCriteriaLine(line) {
      var index = this.criteria.indexOf(line);
      if(index > -1) {
        this.criteria.splice(index, 1);
        delete this.rules[line['Name']];
      }
    },
    addActionsLine(item) {
      var newAction = item ? item : {'Name': '', 'Description': ''};
      this.actions.push(newAction);
      newAction.watcher = this.$watch(
        function () {
          if(this.actions.indexOf(newAction)!==-1)
            return this.actions[this.actions.indexOf(newAction)]['Name'];
          else
            return null;
        }, function(newName, oldName) {
          if(newName===null) {
            newAction.watcher();
            return;
          }
          var performance = this.performanceTable.find(function(p) {return p['Name'] === oldName;})
          performance['Name'] = newName;
      });
      if(!this.performanceTable.find(p => {return p['Name'] === newAction['Name']})) {
        var newPerformance = {'Name': newAction['Name']};
        this.performanceTable.push(newPerformance);
      }
      if(!this.dataFromConnection) this.checkIfDirty('actions')
    },
    removeActionsLine(line) {
      var index = this.actions.indexOf(line);
      if(index > -1) this.actions.splice(index, 1);
      if(!this.cleaningUp) {
        var performance = this.performanceTable.find(function(p) {return p['Name'] === line['Name'];})
        var index = this.performanceTable.indexOf(performance);
        if(index > -1) this.performanceTable.splice(index, 1);
      }
    },
    addReferenceActionsLine(item) {
      var newRef = item ? item : {'Category': '', 'Name': ''};
      this.referenceActions.push(newRef);
      if(!this.dataFromConnection) this.checkIfDirty('referenceActions');
    },
    removeReferenceActionsLine(line, categoryName) {
      var index = this.referenceActions.indexOf(line);
      if(index > -1) this.referenceActions.splice(index, 1);
    },
    addWeights() {
      if(this.weights.length == 0) {
        this.weights.push({});
        document.getElementById("new1").style.display = "none";
      } 
      if(!this.dataFromConnection) this.checkIfDirty('weights');
    },
    addThresholds() {
      if(this.thresholds.length == 0) {
        this.thresholds.push({'Threshold': "Preference"},{'Threshold': "Indifference"},{'Threshold': "Veto"});
        document.getElementById("new2").style.display = "none";
      } 
      if(!this.dataFromConnection) this.checkIfDirty('thresholds');
    },
    close () {
      this.saveModuleData();
      this.showModal=false;
    }
  },
  mounted: function () {
    EventBus.$on('show-modal-'+this.id, tab => {
      this.showModal = true;
      var tabId = this.tabsData.indexOf(tab);
      if(tabId!==-1) {
        this.activeTab = 0;
        this.activeTabData = tabId;
      } else {
        var tabId = this.tabsPreferences.indexOf(tab);
        if(tabId!==-1) {
          this.activeTab = 1;
          this.activeTabPreferences = tabId;
        } else {
          this.activeTab = 0;
          this.activeTabPreferences = 0;
        }
      }
    });
    EventBus.$on('update-module-'+this.id, mod => {
      this.setPropertyFromInputFile(mod);
    });
    this.saveModuleData();
  }
}
</script>

<style scoped>
  #subheader {
    margin: 20px;
    font-size: 30px;
    line-height: 1.5;
  }
  * {
    -moz-box-sizing: border-box;
    -webkit-box-sizing: border-box;
    box-sizing: border-box;
  }
  table.v-table tbody td:first-child, table.v-table tbody td:not(:first-child), table.v-table tbody th:first-child, table.v-table tbody th:not(:first-child), table.v-table thead td:first-child, table.v-table thead td:not(:first-child), table.v-table thead th:first-child, table.v-table thead th:not(:first-child) {
    padding: 0 10px
  }
  table.v-table thead th {
    background-color: #f2f2f2;
    max-width: 150px;
    white-space: initial;
    word-wrap: break-word;
    color: #333333 !important;
    font-size: 13px;
    font-weight: bold;
    border-left: 1px dotted LightGray;
    border-right: 1px dotted LightGray;
    border-bottom: 1px solid LightGray;
  }
  table.v-table tbody td {
    font-family: 'Roboto Mono', sans-serif !important;
    word-wrap: break-word;
    font-size: 12px;
    font-weight: normal;
    border-left: 1px dotted LightGray;
    border-right: 1px dotted LightGray;
  }
  .v-input {
    font-size: 12px;
  }
  .v-text-field {
    padding-top: 0px;
    margin-top: 0px;
  }
  .column-wrap {
    width: 1px;
    white-space: nowrap;
  }
</style>



// WEBPACK FOOTER //
// src/components/Workspace/Methods/ELECTRETRINCModal.vue